import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import "./palette.css";
import Icon from "../ui/Icon";

/**
 * Command palette. `commands`: [{ id, group, label, hint?, keywords?, run }].
 * ARIA combobox + listbox pattern: DOM focus stays in the input, the highlighted
 * option is exposed through aria-activedescendant. <dialog> supplies the focus
 * trap, Esc handling and focus restoration.
 */
const Palette = ({ open, onClose, commands }) => {
  const dialogRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const uid = useId();
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) =>
      `${c.label} ${c.group} ${c.keywords ?? ""}`.toLowerCase().includes(q)
    );
  }, [commands, query]);

  // Sync the native dialog with the `open` prop.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setQuery("");
      setIndex(0);
      dialog.showModal();
      inputRef.current?.focus();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => setIndex(0), [query]);

  // Keep the active option in view while arrowing through a long list.
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [index, results]);

  const run = (command) => {
    onClose();
    // Let the dialog release focus first so navigation/scroll behaves normally.
    window.setTimeout(command.run, 0);
  };

  const onKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(results[index]);
    }
  };

  const listId = `${uid}-list`;
  const optionId = (i) => `${uid}-opt-${i}`;
  let lastGroup = null;

  return (
    <dialog
      ref={dialogRef}
      className="palette"
      aria-label="Command palette"
      onClose={onClose}
      onClick={(e) => e.target === dialogRef.current && onClose()}
    >
      <div className="palette__box">
        <div className="palette__field">
          <Icon name="search" />
          <input
            ref={inputRef}
            name="palette-query"
            className="palette__input"
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={results.length ? optionId(index) : undefined}
            aria-autocomplete="list"
            placeholder="Search projects, sections, links…"
            autoComplete="off"
            spellCheck={false}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <kbd aria-hidden="true">Esc</kbd>
        </div>

        <ul id={listId} ref={listRef} className="palette__list" role="listbox" aria-label="Results">
          {results.map((c, i) => {
            const showGroup = c.group !== lastGroup;
            lastGroup = c.group;
            return (
              <React.Fragment key={c.id}>
                {showGroup && (
                  <li className="palette__group" role="presentation">
                    {c.group}
                  </li>
                )}
                <li
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === index}
                  className="palette__item"
                  onMouseMove={() => setIndex(i)}
                  onClick={() => run(c)}
                >
                  <span>{c.label}</span>
                  {c.hint && <span className="palette__hint">{c.hint}</span>}
                </li>
              </React.Fragment>
            );
          })}
        </ul>

        <p className="palette__empty" role="status" hidden={results.length > 0}>
          No matches for “{query}”.
        </p>
      </div>
    </dialog>
  );
};

export default Palette;
